import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { api, background, LICENCE_REQUIRED_EVENT } from "../api/client";
import { GenerexMark } from "../components/GenerexMark";
import { Callout } from "../components/Callout";
import { Motion, Presence } from "../components/presence";
import { Button } from "../components/ui";
import { UnsavedChangesHost } from "../components/UnsavedChangesHost";
import { ActivationScreen } from "./ActivationScreen";
import { reloadUiState } from "../auth/auth";
import { AuthGate } from "../auth/AuthGate";
import { mountedScope } from "../auth/mounted";
import { t } from "../i18n/i18n";
import { gateState, type GateScreen, type GateState, LICENCE_QUERY, overlayTransition, reloadForLicence, rememberPoste } from "./licence";
import { IdentityScreen } from "./IdentityScreen";
import { PrivacyConsent } from "./PrivacyConsent";
import { SuspendedScreen } from "./SuspendedScreen";
import { UsageTracker } from "./usageTracker";
import { licenceScope } from "../state/licenceScope";
import { useUi } from "../state/ui";
import { installUnloadGuard, unsavedDrafts } from "../state/unsavedDrafts";
import { drainOrderEditors, resumeReadOnlyQueues, retryOrderQueues } from "../views/order/orderSaveQueue";
import type { LicenceStatus } from "../api/types";
import { licenceStatusSeen, readOnlyTransition } from "./readOnly";
import { markTrialPrint, trialExport } from "./trial";
import { TrialTipHost } from "./TrialTip";
import { Waiting } from "../components/Waiting";

/** N'ouvre l'application qu'à un poste titulaire d'une licence valide, notice de protection des données
 *  acceptée ; sinon, l'écran d'activation ou la notice.
 *
 *  Licence ou place du poste perdue en cours de session (place reprise, expiration, contrôle périodique : audit de
 *  persistance M6) : les saisies en cours sont d'abord recopiées dans le navigateur, puis l'écran d'activation
 *  s'affiche PAR-DESSUS l'application, qui reste montée avec ses saisies. Réactivée avec la même licence,
 *  l'application reprend telle quelle et les enregistrements de l'OPO en attente repartent ; avec une autre
 *  licence, la page est rechargée sur elle. */
export function LicenceGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, error, refetch, isFetching } = useQuery({
    queryKey: LICENCE_QUERY,
    // Relecture de fond (docs/17 §4.5) : page cachée, elle ne compte pas comme une activité de la personne.
    queryFn: () => background(api.licence),
    staleTime: 60_000,
    // Relue toutes les deux minutes (licence d'essai reconnue par le relevé du serveur, renouvellement d'une licence
    // expirée en lecture seule) : le poste suit son état sans rechargement (essai du 30/10/2026 : 15 min, trop long).
    refetchInterval: 2 * 60_000,
  });

  // Une route métier a refusé l'accès (licence expirée, poste libéré…) : l'état est relu aussitôt.
  useEffect(() => {
    const onRequired = () => void queryClient.invalidateQueries({ queryKey: LICENCE_QUERY });
    window.addEventListener(LICENCE_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(LICENCE_REQUIRED_EVENT, onRequired);
  }, [queryClient]);

  // Saisies non enregistrées : confirmation avant de fermer ou recharger l'onglet, copies au passage en arrière-plan.
  useEffect(() => installUnloadGuard(), []);

  // Trace du poste dans ce navigateur, pour reconnaître plus tard un cookie de poste perdu.
  useEffect(() => {
    if (data?.enforced && data.licensed) rememberPoste(data);
  }, [data]);

  // Licence d'essai (registre F44) : l'impression du navigateur ne rend pas les documents.
  useEffect(() => markTrialPrint(trialExport(data)), [data]);

  // Données du navigateur rangées sous la licence du poste (M8) ; autre licence : rechargement (m6). Licence en mode
  // comptes (docs/17 §4.8) : la portée de la personne est choisie par le portail des comptes, celle de la licence ne
  // sert qu'à reconnaître une autre licence.
  const ref = data?.space_ref ?? null;
  const accountsMode = data?.accounts?.mode === "accounts";
  const switching = licenceScope.differs(ref);
  // Poste déjà ouvert au chargement de la page (son cookie précède la page) : les clés d'avant le rangement sont les
  // siennes et passent toutes sous sa licence. Ouvert seulement par une activation faite dans cette page : rien ne
  // dit à quelle licence elles appartenaient, seules celles liées à un dossier passent (`adopt`, `inherit`).
  const openAtLoad = useRef<boolean | null>(null);
  if (data && openAtLoad.current === null) openAtLoad.current = Boolean(data.space_ref);
  useEffect(() => {
    if (!ref) return;
    const inherit = openAtLoad.current === true;
    // Application montée pour un compte : sa portée ne change pas sous elle (§4.8), même si la licence relue passe en
    // repli ou en mode hérité ; le changement attend le rechargement.
    const adopted = licenceScope.adopt(ref, { inherit, accounts: accountsMode || mountedScope() !== null });
    if (adopted === "changed") reloadForLicence(queryClient);
    else if (adopted === "switched") reloadUiState(); // portée d'une personne quittée (mode hérité, repli)
    else if (adopted === "migrated" && !inherit) {
      // Dossier ouvert relu au démarrage sous l'ancienne clé commune, d'une licence inconnue : oublié avant que
      // l'application ne s'ouvre (et ne le range sous celle-ci).
      const init = useUi.getInitialState();
      useUi.setState({ exerciseId: init.exerciseId, mapSlug: init.mapSlug, view: init.view, opo: init.opo, phase: init.phase });
    }
  }, [ref, accountsMode, queryClient]);

  // L'application a été ouverte dans cette page : elle reste montée sous l'écran d'activation.
  const [opened, setOpened] = useState(false);
  const gate = gateState(data, opened, switching);
  if (gate.view === "app" && !opened) setOpened(true);

  // Bascule vers l'écran d'activation : chaque saisie est d'abord recopiée dans le navigateur (OPO compris) ; au
  // retour, les envois de l'OPO repartent sans attendre leur délai et l'avis dit que tout est intact.
  const shownOverlay = useRef<GateState["overlay"]>(null);
  useEffect(() => {
    const step = overlayTransition(shownOverlay.current, gate.overlay);
    shownOverlay.current = gate.overlay;
    if (step === "snapshot") {
      drainOrderEditors();
      unsavedDrafts.snapshotAll();
    } else if (step === "resume" && !switching) {
      retryOrderQueues();
      useUi.getState().notify({
        text: t("Poste réactivé"),
        detail: t("Vos saisies en cours sont intactes. L'OPO reprend son enregistrement ; ailleurs, enregistrez de nouveau ce qui a été refusé."),
        tone: "ok",
      });
    }
  }, [gate.overlay, switching]);

  // Lecture seule d'une licence expirée (décision d'Alexis du 03/10/2026) : hors d'elle (licence active ou fermée),
  // la prochaine lecture seule s'expliquera de nouveau ; à son début, rien n'est remis à zéro (le refus qui a fait
  // relire l'état l'a peut-être déjà expliquée : revue du 03/10/2026). À son retour au travail (renouvellement, même
  // code), les files de l'OPO arrêtées par elle repartent et l'avis le dit. Rien à ressaisir.
  const seenStatus = useRef<LicenceStatus | undefined>(undefined);
  useEffect(() => {
    const step = readOnlyTransition(seenStatus.current, data);
    if (data) seenStatus.current = data;
    licenceStatusSeen(data);
    if (step === "resume") {
      resumeReadOnlyQueues();
      useUi.getState().notify({
        text: t("Licence renouvelée"),
        detail: t("Generex est de nouveau modifiable. L'OPO reprend son enregistrement ; ailleurs, enregistrez de nouveau ce qui a été refusé."),
        tone: "ok",
      });
    }
  }, [data]);

  // Écran par-dessus l'application : le clavier ne l'atteint plus. Échap ne ferme rien en dessous (fenêtre,
  // confirmation : la pile d'Échap écoute le document) ; les raccourcis (⌘K, ⌘Z, « ? ») ne partent ni d'un champ de
  // l'écran (arrêtés à sa racine, `onKeyDown`) ni d'ailleurs (arrêtés ici, avant le document).
  const blocked = gate.view === "app" && gate.overlay !== null;
  const overlayBox = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!blocked) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || !overlayBox.current?.contains(e.target as Node)) e.stopPropagation();
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [blocked]);

  if (data && gate.view === "activation") return <ActivationScreen status={data} />;
  if (data && gate.view === "privacy") return <PrivacyConsent version={data.privacy_version} />;
  if (data && gate.view === "identity") return <IdentityScreen status={data} />;
  if (data && gate.view === "suspended") return <SuspendedScreen />;
  if (data && gate.view === "app") {
    return (
      <>
        {/* Sans boîte (`contents`) : la mise en page de l'application est inchangée ; inerte sous l'écran. */}
        <div className="contents" inert={blocked} aria-hidden={blocked || undefined}>
          <AuthGate licence={data}>{children}</AuthGate>
        </div>
        <Presence show={blocked}>
          {blocked && (
            // Au-dessus des fenêtres et des confirmations de l'application (portails z-50 et z-90), qu'il recouvre ;
            // un menu resté ouvert (z-100) se ferme au premier clic sur l'écran.
            <Motion
              ref={overlayBox}
              origin="fade"
              className="absolute inset-0 z-[95] overflow-y-auto bg-canvas"
              onKeyDown={(e) => e.stopPropagation()}
            >
              <GateOverlay screen={gate.overlay} status={data} />
            </Motion>
          )}
        </Presence>
        <UnsavedChangesHost />
        {/* Usage de l'interface (vues ouvertes, temps passé) : seulement application ouverte et sans écran par-dessus. */}
        {!blocked && <UsageTracker status={data} />}
        {/* Licence d'essai : bulle « Export impossible… » au survol de tout geste d'export grisé. */}
        {trialExport(data) && <TrialTipHost />}
      </>
    );
  }

  return (
    <Motion origin="fade" className="flex min-h-full flex-col items-center justify-center gap-4 bg-canvas p-6 text-center">
      <GenerexMark size={40} />
      {switching ? (
        <p className="text-ui text-ink-muted">{<Waiting>{t("Ouverture de la licence…")}</Waiting>}</p>
      ) : error ? (
        <>
          <Callout tone="danger" className="max-w-[360px] text-left">
            {t("Le serveur Generex ne répond pas. Vérifiez la connexion du poste, puis réessayez.")}
          </Callout>
          <Button variant="secondary" onClick={() => void refetch()} disabled={isFetching}>
            {t("Réessayer")}
          </Button>
        </>
      ) : (
        <p className="text-ui text-ink-muted">{<Waiting>{t("Vérification de la licence…")}</Waiting>}</p>
      )}
    </Motion>
  );
}

/** Écran posé par-dessus l'application (licence perdue, notice, identité, suspension en cours de session). */
function GateOverlay({ screen, status }: { screen: GateScreen | null; status: LicenceStatus }) {
  if (screen === "activation") return <ActivationScreen status={status} kept />;
  if (screen === "privacy") return <PrivacyConsent version={status.privacy_version} kept />;
  if (screen === "identity") return <IdentityScreen status={status} kept />;
  if (screen === "suspended") return <SuspendedScreen kept />;
  return null;
}
